import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { ArrowRight, ExternalLink, Sparkles, X } from 'lucide-react';
import styles from './Spotlight.module.sass';
import InfoPage from '../InfoPage';
import { getCloudinaryUrl } from '../../utils/cloudinary';
import {
  SPOTLIGHT_SECTIONS,
  formatSpotlightAnswer,
  isSpotlightQuestionVisible,
} from '../../data/spotlightQuestions';

const COPY = {
  creator: {
    title: 'Creator Spotlight',
    heroBadge: 'Creator of the Week',
    blurb: 'Every week we shine a light on one Postaway creator. Meet the people behind the content.',
    questionnairePath: '/creators/get-featured',
    emptyTitle: 'Our first Creator Spotlight is coming soon.',
    ctaTitle: 'Want to be in the spotlight?',
    ctaText: 'Fill out the Creator Spotlight questionnaire to be featured in an upcoming newsletter.',
  },
  host: {
    title: 'Host Spotlight',
    heroBadge: 'Host of the Week',
    blurb: 'Every week we shine a light on one Postaway host. Meet the people behind the properties.',
    questionnairePath: '/hosts/get-featured',
    emptyTitle: 'Our first Host Spotlight is coming soon.',
    ctaTitle: 'Want to be in the spotlight?',
    ctaText: 'Fill out the Host Spotlight questionnaire to be featured in an upcoming newsletter.',
  },
};

/** Parse a DATE (YYYY-MM-DD) or timestamp into a local Date without TZ drift. */
function toLocalDate(value) {
  if (!value) return null;
  const str = String(value);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatSpotlightDate(item, { withYear = false } = {}) {
  const d = toLocalDate(item.spotlight_date) || toLocalDate(item.published_at) || toLocalDate(item.created_at);
  if (!d) return '';
  return d.toLocaleDateString(undefined, withYear ? { month: 'long', day: 'numeric', year: 'numeric' } : { month: 'long', day: 'numeric' });
}

function firstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || 'them';
}

function optimized(url, size) {
  if (!url) return '';
  return getCloudinaryUrl(url, { width: size, height: size, crop: 'fill', gravity: 'face', quality: 'auto', format: 'auto' }) || url;
}

const SpotlightModal = ({ item, type, onClose }) => {
  const sections = SPOTLIGHT_SECTIONS[type] || SPOTLIGHT_SECTIONS.creator;
  const answers = item.answers || {};

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose} role="presentation">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`All about ${item.name}`}>
        <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close"><X size={18} /></button>
        <div className={styles.modalHeader}>
          {item.headshot_url && <img className={styles.modalAvatar} src={optimized(item.headshot_url, 240)} alt={item.name} />}
          <div>
            <div className={styles.modalDate}>{formatSpotlightDate(item, { withYear: true })}</div>
            <h3 className={styles.modalName}>{item.name}</h3>
            {item.ig_handle && (
              <a className={styles.modalHandle} href={`https://instagram.com/${item.ig_handle}`} target="_blank" rel="noopener noreferrer">@{item.ig_handle}</a>
            )}
          </div>
        </div>

        {item.email_url && (
          <a className={styles.newsletterBtn} href={item.email_url} target="_blank" rel="noopener noreferrer">
            Read the full spotlight in our newsletter <ExternalLink size={14} />
          </a>
        )}

        {Array.isArray(item.property_image_urls) && item.property_image_urls.length > 0 && (
          <div className={styles.modalPhotos}>
            {item.property_image_urls.map((url) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer" className={styles.modalPhoto}>
                <img src={getCloudinaryUrl(url, { width: 480, height: 360, crop: 'fill', quality: 'auto', format: 'auto' }) || url} alt="Property" />
              </a>
            ))}
          </div>
        )}

        <div className={styles.qa}>
          {sections.map((sec) => {
            const visible = sec.questions.filter((q) => isSpotlightQuestionVisible(q, answers) && formatSpotlightAnswer(q, answers[q.id]));
            if (!visible.length) return null;
            return (
              <div key={sec.section} className={styles.qaSection}>
                <h4 className={styles.qaSectionTitle}>{sec.section}</h4>
                {visible.map((q) => (
                  <div key={q.id} className={styles.qaItem}>
                    <div className={styles.qaQ}>{q.label}</div>
                    <div className={styles.qaA}>{formatSpotlightAnswer(q, answers[q.id])}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

const Spotlight = ({ type = 'creator' }) => {
  const copy = COPY[type] || COPY.creator;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openItem, setOpenItem] = useState(null);

  const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4000' : '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${apiBase}/api/spotlight/list?type=${type}`);
        const data = await res.json();
        if (!cancelled) {
          if (res.ok && data.ok) setItems(data.spotlights || []);
          else setError('We couldn\u2019t load spotlights right now.');
        }
      } catch (err) {
        console.error('Failed to load spotlights:', err);
        if (!cancelled) setError('We couldn\u2019t load spotlights right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, type]);

  const hero = items[0] || null;
  const rest = useMemo(() => items.slice(1), [items]);

  const openLink = (item) => {
    if (item.email_url) {
      window.open(item.email_url, '_blank', 'noopener,noreferrer');
    } else {
      setOpenItem(item);
    }
  };

  const content = (
    <div className={styles.page}>
      <div className={styles.container}>
        <p className={styles.blurb}>{copy.blurb}</p>

        {loading ? (
          <div className={styles.loading}>Loading spotlights…</div>
        ) : error ? (
          <div className={styles.loading}>{error}</div>
        ) : !hero ? (
          <div className={styles.empty}>
            <Sparkles size={36} />
            <h3>{copy.emptyTitle}</h3>
            <p>{copy.ctaText}</p>
            <Link to={copy.questionnairePath} className={styles.primaryBtn}>Fill out the questionnaire <ArrowRight size={16} /></Link>
          </div>
        ) : (
          <>
            {/* Current spotlight */}
            <section className={styles.hero}>
              <div className={styles.heroBadge}><Sparkles size={13} /> {copy.heroBadge}</div>
              <button
                type="button"
                className={styles.heroImageBtn}
                onClick={() => openLink(hero)}
                title={hero.email_url ? 'Read the newsletter spotlight' : `All about ${firstName(hero.name)}`}
              >
                <img className={styles.heroImage} src={optimized(hero.headshot_url, 720)} alt={hero.name} />
              </button>
              {hero.ig_handle ? (
                <a className={styles.heroHandle} href={`https://instagram.com/${hero.ig_handle}`} target="_blank" rel="noopener noreferrer">@{hero.ig_handle}</a>
              ) : (
                <div className={styles.heroHandle}>{hero.name}</div>
              )}
              <div className={styles.heroLinks}>
                <button type="button" className={styles.heroLink} onClick={() => setOpenItem(hero)}>
                  All about {firstName(hero.name)}
                </button>
                {hero.email_url && (
                  <a className={styles.heroLink} href={hero.email_url} target="_blank" rel="noopener noreferrer">
                    Read the newsletter <ExternalLink size={13} />
                  </a>
                )}
              </div>
            </section>

            {/* Hall of fame */}
            {rest.length > 0 && (
              <section className={styles.hall}>
                <div className={styles.hallRule} />
                <h3 className={styles.hallTitle}>Past spotlights</h3>
                <div className={styles.grid}>
                  {rest.map((item) => (
                    <div key={item.id} className={styles.card}>
                      <div className={styles.cardDate}>{formatSpotlightDate(item)}</div>
                      <button
                        type="button"
                        className={styles.cardImageBtn}
                        onClick={() => openLink(item)}
                        title={item.email_url ? 'Read the newsletter spotlight' : `All about ${firstName(item.name)}`}
                      >
                        <img className={styles.cardImage} src={optimized(item.headshot_url, 400)} alt={item.name} loading="lazy" />
                      </button>
                      <div className={styles.cardHandle}>{item.ig_handle ? `@${item.ig_handle}` : item.name}</div>
                      <button type="button" className={styles.cardLink} onClick={() => setOpenItem(item)}>All about {firstName(item.name)}</button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className={cn(styles.cta)}>
              <div>
                <strong>{copy.ctaTitle}</strong>
                <p>{copy.ctaText}</p>
              </div>
              <Link to={copy.questionnairePath} className={styles.primaryBtn}>Fill out the questionnaire <ArrowRight size={16} /></Link>
            </section>
          </>
        )}
      </div>

      {openItem && <SpotlightModal item={openItem} type={type} onClose={() => setOpenItem(null)} />}
    </div>
  );

  return (
    <InfoPage
      title={copy.title}
      divider={false}
      below={content}
      disableStamps
      tallOverride={false}
      customMinHeight={undefined}
    />
  );
};

export default Spotlight;
